import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  CircularProgress
} from '@mui/material'
import {
  RestoreFromTrash,
  Delete,
  Visibility,
  DeleteForever
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

const ProcedimientosEliminados = () => {
  const navigate = useNavigate()
  const [eliminados, setEliminados] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [selectedProcedimiento, setSelectedProcedimiento] = useState(null)

  useEffect(() => {
    loadEliminados()
  }, [])

  const loadEliminados = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/procedimientos/eliminados/lista', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setEliminados(response.data.procedimientos_eliminados || [])
    } catch (error) {
      setError('Error al cargar procedimientos eliminados: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleRecuperar = async (backup_id, codigo) => {
    if (!window.confirm(`¿Está seguro de recuperar el procedimiento ${codigo}?`)) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.post(
        `/api/procedimientos/eliminados/${backup_id}/recuperar`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )

      setSuccess(`Procedimiento ${codigo} recuperado exitosamente`)
      loadEliminados()
      setTimeout(() => setSuccess(null), 3000)
    } catch (error) {
      setError('Error al recuperar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleVerDetalles = async (backup_id) => {
    try {
      const procedimiento = eliminados.find(p => p.id === backup_id)
      setSelectedProcedimiento(procedimiento)
      setViewDialogOpen(true)
    } catch (error) {
      setError('Error al cargar detalles')
    }
  }

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A'
    const date = new Date(fecha)
    return date.toLocaleString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete />
          Procedimientos Eliminados
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Los procedimientos eliminados se guardan aquí y pueden ser recuperados por los administradores
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {eliminados.length === 0 ? (
        <Alert severity="info">
          No hay procedimientos eliminados
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Título</TableCell>
                <TableCell>Proceso</TableCell>
                <TableCell>Gestión</TableCell>
                <TableCell>Eliminado por</TableCell>
                <TableCell>Fecha de eliminación</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {eliminados.map((proc) => (
                <TableRow key={proc.id} hover>
                  <TableCell>
                    <Chip
                      label={proc.codigo}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {proc.titulo}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {proc.proceso}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {proc.gestion_nombre || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {proc.deleted_by_nombre || 'Sistema'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatFecha(proc.deleted_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, display: 'block' }}>
                      {proc.motivo_eliminacion || 'Sin motivo'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Ver detalles">
                        <IconButton
                          size="small"
                          onClick={() => handleVerDetalles(proc.id)}
                          color="info"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Recuperar procedimiento">
                        <IconButton
                          size="small"
                          onClick={() => handleRecuperar(proc.id, proc.codigo)}
                          color="success"
                        >
                          <RestoreFromTrash />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog de detalles */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalles del Procedimiento Eliminado
        </DialogTitle>
        <DialogContent>
          {selectedProcedimiento && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Código:</Typography>
                <Typography variant="body1">{selectedProcedimiento.codigo}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Título:</Typography>
                <Typography variant="body1">{selectedProcedimiento.titulo}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Proceso:</Typography>
                <Typography variant="body1">{selectedProcedimiento.proceso}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Gestión:</Typography>
                <Typography variant="body1">{selectedProcedimiento.gestion_nombre || 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Eliminado por:</Typography>
                <Typography variant="body1">{selectedProcedimiento.deleted_by_nombre || 'Sistema'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Fecha de eliminación:</Typography>
                <Typography variant="body1">{formatFecha(selectedProcedimiento.deleted_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Motivo de eliminación:</Typography>
                <Typography variant="body1">{selectedProcedimiento.motivo_eliminacion || 'Sin motivo especificado'}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedProcedimiento && (
            <Button
              onClick={() => {
                handleRecuperar(selectedProcedimiento.id, selectedProcedimiento.codigo)
                setViewDialogOpen(false)
              }}
              variant="contained"
              color="success"
              startIcon={<RestoreFromTrash />}
            >
              Recuperar
            </Button>
          )}
          <Button onClick={() => setViewDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProcedimientosEliminados
